/* here&found — <hf-rail> (assets/hf-rail.js).
   Scroll-snap rail behaviour (hero finds, best sellers, related, watch & shop).
   The track is pure CSS scroll-snap and scrolls with JS disabled; this element
   only adds the desktop arrow buttons, which nudge by exactly one card.
   Mobile is swipe-only (sections hide the arrows below the desktop breakpoint).

   Contract with sections:
   - the scroll container is [data-rail-track] (or the element itself);
   - arrow buttons are [data-rail-prev] / [data-rail-next], shipped with the
     `hidden` attribute so they never render when JS is off; hf-rail reveals
     them and keeps their disabled state in sync with the scroll edges.

   prefers-reduced-motion → instant scroll, no smooth glide.
   All listeners are removed on disconnect. No framework. */

class HFRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]') || this;
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.onPrev = () => this.nudge(-1);
    this.onNext = () => this.nudge(1);
    this.onScroll = () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    };
    if (this.prev) {
      this.prev.removeAttribute('hidden');
      this.prev.addEventListener('click', this.onPrev);
    }
    if (this.next) {
      this.next.removeAttribute('hidden');
      this.next.addEventListener('click', this.onNext);
    }
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    if (this.prev) this.prev.removeEventListener('click', this.onPrev);
    if (this.next) this.next.removeEventListener('click', this.onNext);
    this.track.removeEventListener('scroll', this.onScroll);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  step() {
    const card = this.track.firstElementChild;
    if (!card) return this.track.clientWidth;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  nudge(dir) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: dir * this.step(), behavior: reduced ? 'auto' : 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
  }
}

if (!customElements.get('hf-rail')) customElements.define('hf-rail', HFRail);
